import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "@mui/material";
import { AlternateEmail, ArrowBackRounded } from "@mui/icons-material";
import AuthLayout, { authStyles as styles } from "../../components/customComponents/authLayout/AuthLayout";
import OtpBoxInput from "../../components/customComponents/otpInput/OtpBoxInput";
import { authApi } from "../../auth/authApi";
import { setAuth } from "../../store/actions";
import { loginConfig } from "../../auth/config";
import {
  authErrorMessage,
  authStateFromTokenResponse,
  isValidEmail,
  maskEmail,
} from "../../utils/authSession";
import { emailDeliveryFailure } from "../../utils/emailDeliveryResponse";

const OTP_LENGTH = 6;
// Fallback only - the request-OTP endpoint is expected to return the authoritative
// `resendAfterSeconds`. Never let the button be enabled earlier than the server will accept a
// resend, or the user just earns a 429.
const FALLBACK_RESEND_SECONDS = 60;

/**
 * Email + one-time-code sign-in. Two steps, no password anywhere in the flow - which is why there
 * is deliberately no "forgot password" branch here.
 */
export default function LoginOtp() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth?.accessToken);

  const [step, setStep] = useState("email"); // "email" | "verify"
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Stops the auto-submit effect below from firing repeatedly while a verification is in flight.
  const autoVerified = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const handleVerify = useCallback(
    async (otpValue) => {
      const value = String(otpValue || "").trim();
      if (value.length !== OTP_LENGTH) return;

      setError("");
      setNotice("");
      setSubmitting(true);
      try {
        const res = await authApi.verifyLoginOtp({
          email: email.trim().toLowerCase(),
          otp: value,
          rememberMe,
        });
        dispatch(setAuth(authStateFromTokenResponse(res?.data)));
        navigate("/splash", { replace: true }); // splash routes to the role's dashboard
      } catch (err) {
        console.error("OTP verification failed:", err);
        setError(authErrorMessage(err, "That code could not be verified."));
        setOtp("");
        autoVerified.current = false;
        setSubmitting(false);
      }
    },
    [email, rememberMe, dispatch, navigate],
  );

  // A 6-digit code is unambiguous, so verify as soon as the last box is filled rather than making
  // the user reach for a button they can't meaningfully decline.
  useEffect(() => {
    if (step !== "verify") return;
    if (otp.length !== OTP_LENGTH) {
      autoVerified.current = false;
      return;
    }
    if (autoVerified.current) return;
    autoVerified.current = true;
    handleVerify(otp);
  }, [otp, step, handleVerify]);

  if (accessToken) {
    return <Navigate to="/splash" replace />;
  }

  const handleRequestOtp = async (event) => {
    if (event) event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      const res = await authApi.requestLoginOtp({ email: normalizedEmail });
      const data = res?.data || {};
      const deliveryError = emailDeliveryFailure(data);
      if (deliveryError) { setError(deliveryError); return; }
      setMaskedEmail(data.maskedEmail || maskEmail(normalizedEmail));
      setCooldown(Number(data.resendAfterSeconds) || FALLBACK_RESEND_SECONDS);
      setNotice(data.message || "We've emailed you a 6-digit code.");
      setOtp("");
      autoVerified.current = false;
      setStep("verify");
    } catch (err) {
      console.error("OTP request failed:", err);
      setError(authErrorMessage(err, "We couldn't send a code to that address."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setOtp("");
    setCooldown(0);
    setError("");
    setNotice("");
    autoVerified.current = false;
  };

  return (
    <AuthLayout
      heading="Sign in with a code"
      subheading={step === "email" ? "We'll email a 6-digit code to your registered address." : undefined}
      footer={
        loginConfig.methods.length > 1 ? (
          <div className={styles.footerRow}>
            <button type="button" className={styles.linkButton} onClick={() => navigate("/")}>
              <ArrowBackRounded fontSize="inherit" /> Other ways to sign in
            </button>
          </div>
        ) : undefined
      }
    >
      {step === "email" ? (
        <form className={styles.form} onSubmit={handleRequestOtp}>
          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="otp-login-email">
              Email
            </label>
            <input
              id="otp-login-email"
              type="email"
              placeholder="Email address"
              className={styles.inputField}
              value={email}
              onChange={(e) => {
                setError("");
                setEmail(e.target.value.trim());
              }}
              autoComplete="email"
              disabled={submitting}
              autoFocus
            />
            <AlternateEmail className={styles.trailingIcon} />
          </div>

          <div className={styles.actionsRow}>
            <label className={styles.rememberToggle}>
              <input
                type="checkbox"
                className={styles.checkboxInput}
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={submitting}
              />
              <span className={styles.checkboxVisual} aria-hidden="true" />
              <span className={styles.labelText}>Remember me</span>
            </label>
          </div>

          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button type="submit" className={styles.primaryButton} disabled={!email || submitting}>
            {submitting ? "Sending…" : "Send code"}
          </button>
        </form>
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleVerify(otp);
          }}
        >
          <p className={styles.otpMeta}>
            Enter the {OTP_LENGTH}-digit code sent to <strong>{maskedEmail}</strong>
          </p>

          <OtpBoxInput
            value={otp}
            onChange={(next) => {
              setError("");
              setOtp(next);
            }}
            numInputs={OTP_LENGTH}
            disabled={submitting}
          />

          <div className={styles.resendRow}>
            <span>Didn't get the code?</span>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => handleRequestOtp()}
              disabled={cooldown > 0 || submitting}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>

          {notice && !error && (
            <Alert severity="info" className={styles.alert}>
              {notice}
            </Alert>
          )}
          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={otp.length !== OTP_LENGTH || submitting}
          >
            {submitting ? "Verifying…" : "Verify and sign in"}
          </button>

          <div className={styles.centeredRow}>
            <button type="button" className={styles.linkButton} onClick={handleChangeEmail}>
              Use a different email
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
